import React, { useRef } from 'react'
import {
  Box,
  Button,
  Card,
  Columns,
  Container,
  Element,
  Heading,
  Level,
  Section,
  Tag,
} from 'react-bulma-components'
import FormWrapper from '../../components/form/form-wrapper'
import Layout from '../../components/layout/layout'
import { useFormWrapperPropsWithMutationData } from '../../hooks/use-form-wrapper-props-with-mutation-data'
import useUrlId from '../../hooks/use-url-id'
import { useMutationState } from '../../queries'
import {
  useAssignWorkersToPosts,
  useCreateMutationKey,
  useWorkersRequestQuery,
} from '../../queries/workers-requests'
import { localDate } from '../../utils/date'
import Breadcrumbs from '../../components/breadcrumbs/breadcrumbs'
import WorkersRequestsMessages from '../../components/workers-requests/workers-requests-messages'
import useStore from '../../store'
import WorkersRequestPageTitle, {
  EditActions,
} from '../../components/workers-requests/titles/workers-request-page-title'
import { sortBy } from 'lodash'
import locales from '../../../../locales/workers-requests'
import History from '../../components/history/history'
import WorkersRequestHistoryItemCustomContent from '../../components/workers-requests/history/history-item-custom-content'
import WorkPeriodsDatesDescriptionList from '../../components/work-periods/work-periods-dates-description-list'
import useWorkersRequestPostsSuggestions from '../../hooks/use-workers-request-posts-suggestions'
import Protected from '../../components/protected/protected'
import MultipleAssignsForm from '../../components/workers-requests/forms/multiple-assigns-form/multiple-assigns-form'
import WorkersRequestPostListItem from '../../components/workers-requests/posts-list-item'
import { compareDocsById } from '../../utils/documents'
import { WorkersRequestPost } from '../../api/workers-requests'
import Avatar from '../../components/avatar/avatar'

const WorkersRequestsDetails: React.FC = ({}) => {
  const currentUserRole = useStore(state => state.session.currentUserRole)
  const mutationState = useMutationState(useCreateMutationKey())
  const assignWorkersToPostsMutation = useAssignWorkersToPosts()
  const [displayMode, setDisplayMode] = React.useState<'individualAssign' | 'multipleAssign'>(
    'individualAssign',
  )
  const { id, isNew } = useUrlId()
  const { data: workersRequest } = useWorkersRequestQuery(id)
  const formWrapperProps = useFormWrapperPropsWithMutationData(
    formWrapperBaseProps,
    //@ts-expect-error
    mutationState,
    !isNew,
  )

  const breadcrumbs = useBreadcrumbs()
  const historyRef = useRef<null | HTMLDivElement>(null)
  const scrollToHistory = () => historyRef.current?.scrollIntoView()

  const historyItems = React.useMemo(() => {
    return sortBy(workersRequest?.history || [], 'createdAt').reverse()
  }, [workersRequest])

  const data = useWorkersRequestPostsSuggestions({
    workersRequest,
  })

  const showMultipleAssignForm = !Boolean(workersRequest?.isComplete || workersRequest?.isCancelled)

  React.useEffect(() => {
    if (!showMultipleAssignForm && displayMode === 'multipleAssign') {
      setDisplayMode('individualAssign')
    }
  }, [showMultipleAssignForm])

  const assignWorkersToPosts = async (selectedWorkerIds: string[], posts: WorkersRequestPost[]) => {
    if (!workersRequest?._id) return
    const postsWithSelectedWorkerIds = posts
      .map((post, index) => ({
        postId: post._id,
        userId: selectedWorkerIds[index],
      }))
      .filter(post => post.userId && post.postId) as { postId: string; userId: string }[]
    await assignWorkersToPostsMutation.mutateAsync({
      workersRequestId: workersRequest?._id,
      posts: postsWithSelectedWorkerIds,
    })
  }

  const author = workersRequest?.history?.[0]?.by

  if (!workersRequest) return <></>

  return (
    <Layout>
      <Section>
        <FormWrapper {...formWrapperProps}>
          <Container breakpoint="desktop" max={true}>
            <Level>
              <Level.Side>
                <Breadcrumbs items={breadcrumbs} />
              </Level.Side>
              <Level.Side align="right">
                {!isNew && <EditActions workersRequest={workersRequest} />}
              </Level.Side>
            </Level>
            <Level alignItems="flex-start" mb={6}>
              <Level.Side>
                <WorkersRequestPageTitle
                  workersRequest={workersRequest}
                  organizationType={
                    currentUserRole === 'interimAgencyMember' ? 'employer' : 'interimAgency'
                  }
                />
              </Level.Side>
              <Level.Side align="right">
                <Tag.Group>
                  <WorkersRequestsMessages workersRequest={workersRequest} type="tag" />
                </Tag.Group>
              </Level.Side>
            </Level>
            <Container>
              <Columns>
                <Columns.Column mb={1} size={12}>
                  <Level>
                    <Level.Side>
                      <Element>
                        <Heading size={5}>
                          {`Du ${localDate(workersRequest.missionsStart)} au ${localDate(
                            workersRequest.missionsEnd,
                          )}`}
                        </Heading>
                        {author && (
                          <>
                            <Heading size={6} subtitle textWeight="bold">
                              Par <Avatar user={author} />
                            </Heading>
                          </>
                        )}
                      </Element>
                    </Level.Side>
                    <Level.Side>
                      <Button color={'ghost'} onClick={scrollToHistory}>
                        Historique de la Demande&nbsp;↓
                      </Button>
                    </Level.Side>
                  </Level>
                  <Card mb={6}>
                    <Card.Content>
                      <p>
                        <Element mb={2} textWeight="bold">
                          Adresse
                        </Element>
                        {workersRequest.missionData.address}
                      </p>
                      <br />
                      <b>Horaire</b>
                      <WorkPeriodsDatesDescriptionList
                        workPeriods={workersRequest.missionData.workPeriods}
                      />
                    </Card.Content>
                  </Card>
                </Columns.Column>

                <Columns.Column size={12}>
                  <Heading
                    size={5}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Element>
                      Postes demandés ({workersRequest.stats.assigned}&nbsp;/&nbsp;
                      {workersRequest.stats.assigned + workersRequest.stats.notAssigned})
                    </Element>
                    {showMultipleAssignForm && (
                      <Element display="flex" alignItems="center">
                        <Protected roles={['interimAgencyMember']}>
                          <Button
                            size="small"
                            color={displayMode === 'individualAssign' ? 'primary' : 'ghost'}
                            onClick={() => setDisplayMode('individualAssign')}
                            data-test="individual-assign-posts-tab"
                          >
                            Assignation individuelle
                          </Button>
                          <Button
                            size="small"
                            color={displayMode === 'multipleAssign' ? 'primary' : 'ghost'}
                            onClick={() => setDisplayMode('multipleAssign')}
                            data-test="multiple-assign-posts-tab"
                          >
                            Assignation multiple
                          </Button>
                        </Protected>
                      </Element>
                    )}
                  </Heading>
                  <Element>
                    {displayMode === 'multipleAssign' && (
                      <Box>
                        <MultipleAssignsForm
                          onAutoAssign={async (selectedWorkerIds, posts) => {
                            await assignWorkersToPosts(selectedWorkerIds, posts)
                          }}
                          suggestions={data}
                          workersRequest={workersRequest}
                        />
                      </Box>
                    )}
                    {displayMode === 'individualAssign' &&
                      workersRequest.posts.map(post => (
                        <Element key={post._id}>
                          <WorkersRequestPostListItem
                            post={post}
                            workersRequest={workersRequest}
                            count={{
                              current:
                                workersRequest.posts.findIndex(p => compareDocsById(p, post)) + 1,
                              total: workersRequest.stats.posts.total,
                            }}
                            suggestions={data}
                          />
                        </Element>
                      ))}
                    {workersRequest.posts.length === 0 && (
                      <Element textSize={7} textAlign="center">
                        Aucun poste à afficher
                      </Element>
                    )}
                  </Element>
                </Columns.Column>
              </Columns>
            </Container>
            <Columns mb={5}></Columns>
          </Container>
          {/* @ts-expect-error */}
          <Container breakpoint="desktop" max={true} pt={5} domRef={historyRef}>
            <Heading size={5} pt={5}>
              Historique
            </Heading>
            <History
              //@ts-expect-error
              items={historyItems}
              locales={locales.history}
              renderCustomContent={item => (
                <WorkersRequestHistoryItemCustomContent
                  key={item._id}
                  historyItem={historyItems.find(i => i._id === item._id)}
                />
              )}
            />
          </Container>
        </FormWrapper>
      </Section>
    </Layout>
  )
}

export default WorkersRequestsDetails

const formWrapperBaseProps = {
  loadingProps: { body: 'Enregistrement de la Demande...' },
  successProps: {
    title: 'Demande créée',
    body: {
      description: "La Demande a été créée, l'Agence d'Intérim a été notifiée.",
      actions: [
        {
          label: 'Nouvelle Demande',
        },
        {
          label: 'Retourner à la liste',
          to: '/workers-requests',
        },
      ],
    },
  },
}

const useBreadcrumbs = () =>
  React.useMemo(
    () => [
      {
        label: 'Demandes de Personnel',
        to: '/workers-requests',
      },
      {
        label: `Demande de Personnel`,
      },
    ],
    [],
  )
